import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/dialog';
import { Loader2, MapPin, Route } from 'lucide-react';
import { useTerminalRoute } from '@/domains/terminals/services/account-user-terminals';
import { H3RouteMap } from '@/shared/components/H3RouteMap';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import EmptyView from '@/shared/components/EmptyView';
import { formatDateToLocaleString } from '@/shared/utils/date-utils';

/** Time range for the terminal route dialog */
const TimeRange = ['15m', '30m', '1h', '3h', '6h', '12h', '1d', '3d', '7d', '15d'] as const;

/** Time range options for the terminal route dialog */
const timeRangeOptions: { value: typeof TimeRange[number]; label: string }[] = TimeRange.map((value) => ({ value, label: value.replace('m', ' Minutes').replace('h', ' Hours').replace('d', ' Days') }));

interface TerminalRouteDialogProps {
  open: boolean;
  terminalId: string;
  onOpenChange: (open: boolean) => void;
}

export const TerminalRouteDialog = ({
  open,
  onOpenChange,
  terminalId,
}: TerminalRouteDialogProps) => {
  const [timeRange, setTimeRange] = useState<typeof TimeRange[number]>('1d');
  const { data: routePoints, isLoading } = useTerminalRoute(terminalId, timeRange, open);

  const routeSummary = useMemo(() => {
    if (!routePoints?.length) return null;
    const start = routePoints[0];
    const end = routePoints[routePoints.length - 1];
    return {
      stops: routePoints.length,
      startTime: start.utcTimestamp ? formatDateToLocaleString(start.utcTimestamp) : null,
      endTime: end.utcTimestamp ? formatDateToLocaleString(end.utcTimestamp) : null,
    };
  }, [routePoints]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-4 border-b shrink-0">
          <div className="flex items-start justify-between gap-4 pr-8">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Route className="h-5 w-5" />
                Terminal Route
              </DialogTitle>
              <DialogDescription>
                Terminal movement path over the selected time range.
              </DialogDescription>
              {routeSummary && (
                <p className="text-xs text-muted-foreground mt-2">
                  {routeSummary.stops} point{routeSummary.stops === 1 ? '' : 's'}
                  {routeSummary.startTime && routeSummary.endTime
                    ? ` · ${routeSummary.startTime} → ${routeSummary.endTime}`
                    : ''}
                </p>
              )}
            </div>
            <Select value={timeRange} onValueChange={(value) => setTimeRange(value as typeof TimeRange[number])}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 p-4">
          {isLoading ? (
            <div className="flex h-full items-center justify-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Loading terminal route...</span>
            </div>
          ) : routePoints?.length ?? 0 > 0 ? (
            <H3RouteMap points={routePoints} height="100%" fitOnResize />
          ) : (
            <EmptyView
              icon={MapPin}
              size="large"
              title="No route data"
              description="No H3 location points were found for this terminal in the selected time range."
              className="h-full"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
